
import React from 'react';
import { Button } from "@/components/ui/button";
import DataTable from '@/components/DataTable';
import UploadHeader from '@/components/upload/UploadHeader';
import NavigationSection from '@/components/upload/NavigationSection';
import SelectionControls from '@/components/upload/SelectionControls';
import UploadControls from '@/components/upload/UploadControls';
import AdminHeader from '@/components/layout/AdminHeader';
import AuthGuard from '@/components/auth/AuthGuard';
import { useUploadState } from '@/hooks/useUploadState';
import { useUploadMutations } from '@/hooks/useUploadMutations';
import { useUploadHandlers } from '@/components/upload/handlers/useUploadHandlers';

const Upload = () => {
  const state = useUploadState();
  const mutations = useUploadMutations(
    state.resetForm, 
    state.refetchLeagues, 
    state.refetchYears, 
    state.selectedCountry
  );
  
  const handlers = useUploadHandlers({
    tableData: state.tableData,
    setTableData: state.setTableData,
    sortColumn: state.sortColumn,
    setSortColumn: state.setSortColumn,
    sortDirection: state.sortDirection,
    setSortDirection: state.setSortDirection,
    stateName: state.stateName,
    selectedCountry: state.selectedCountry,
    selectedLeague: state.selectedLeague,
    selectedYear: state.selectedYear,
    saveStateMutation: mutations.saveStateMutation
  });

  if (state.isError) {
    return <div className="text-center p-4">Error loading data. Please try again.</div>;
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-50">
        <AdminHeader />
        <UploadHeader />

        <div className="container mx-auto px-4 py-8">
          <NavigationSection />

          <div className="space-y-4">
            <UploadControls 
              onDataLoaded={handlers.handleDataLoaded} 
              onClearTable={state.resetForm} 
              hasData={state.tableData.length > 0} 
            />

            <SelectionControls
              stateName={state.stateName}
              onStateNameChange={state.setStateName}
              selectedCountry={state.selectedCountry}
              onCountryChange={state.setSelectedCountry}
              selectedLeague={state.selectedLeague}
              onLeagueChange={state.setSelectedLeague}
              selectedYear={state.selectedYear}
              onYearChange={state.setSelectedYear}
              countries={state.countries}
              leagues={state.leagues}
              years={state.years}
              newCountry={state.newCountry}
              onNewCountryChange={state.setNewCountry}
              onAddCountry={() => mutations.addCountryMutation.mutate(state.newCountry)}
              newLeague={state.newLeague}
              onNewLeagueChange={state.setNewLeague}
              onAddLeague={() => mutations.addLeagueMutation.mutate(state.newLeague)}
              newYear={state.newYear}
              onNewYearChange={state.setNewYear}
              onAddYear={() => mutations.addYearMutation.mutate(state.newYear)}
            />

            {(state.tableData.length > 0 || state.selectedCountry || state.selectedLeague || state.selectedYear) && (
              <div className="flex justify-end">
                <Button 
                  className="bg-slate-700 hover:bg-slate-600" 
                  onClick={handlers.handleSaveState} 
                  disabled={state.tableData.length === 0 || !state.selectedCountry || !state.selectedLeague || !state.selectedYear}
                >
                  Save State
                </Button>
              </div>
            )}

            {state.tableData.length > 0 ? (
              <div className="mt-8">
                <DataTable 
                  data={state.tableData} 
                  onSort={handlers.handleSort} 
                  sortColumn={state.sortColumn} 
                  sortDirection={state.sortDirection} 
                />
              </div>
            ) : (
              <div className="mt-8 text-center text-gray-500">
                No data available. Please upload an Excel file.
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthGuard>
  );
};

export default Upload;
